import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "expo-router";
import {
  type ComponentRef,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import {
  Keyboard,
  NativeSyntheticEvent,
  Platform,
  TextInputKeyPressEventData,
  TextInputProps,
  TextInputSelectionChangeEventData,
  useWindowDimensions,
} from "react-native";
import { useDebouncedCallback } from "use-debounce";
import * as yup from "yup";

import SignInView, { IForm, makeMobileNumberField } from "./SignInView";
import { useAuth } from "../../providers/Auth";

import { TextInput } from "@/components/atoms/TextInput";
import { validateSignInPhoneNumber } from "@/features/auth/signInCountryPolicy";
import { SignInResult, useAuthSignInMutation } from "@/generated/graphql";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { useToastNotification } from "@/hooks/useToastNotification";
import { useTheme } from "@/providers/ThemeProvider";
import { formatError } from "@/utils/formatError";

/**
 * Validation schema for SignIn form with country-specific phone number validation
 *
 * Supports multiple country codes with different formatting requirements:
 * - +1 (USA): (XXX) XXX-XXXX
 * - +44 (UK): XXXX XXXXXX
 * - +52 (Mexico): XX XXXX XXXX
 * - +55 (Brazil): XX XXXXX-XXXX
 * - +39 (Italy): 3XX XXX XXXX (mobile-only)
 * - +351 (Portugal): XX XXX XXXX
 * - +32 (Belgium): XXX XXX XXX
 * - +30 (Greece): XXX XXX XXXX
 * - +7 (Russia): (XXX) XXX-XX-XX
 * - +380 (Ukraine): XX XXX XXXX
 * - +46 (Sweden): XX XXX XXXX
 * - +40 (Romania): XX XXX XXXX
 * - +358 (Finland): XX XXX XXXX
 * - +593 (Ecuador): XX XXX XXXX
 * - +966 (Saudi Arabia): XX XXX XXXX
 * - +971 (UAE): XX XXX XXXX
 * - +64 (New Zealand): XX XXX XXXX
 * - +41 (Switzerland): XX XXX XX XX
 * - +43 (Austria): XXX XXXXX
 * - +420 (Czech Republic): XXX XXX XXX
 * - +48 (Poland): XXX XXX XXX
 * - +36 (Hungary): XX XXX XXX
 * - +359 (Bulgaria): XX XXX XXX
 * - +386 (Slovenia): XX XXX XXX
 * - +357 (Cyprus): XXXX XXXX
 * - +598 (Uruguay): XXXX XXXX
 * - +506 (Costa Rica): XXXX XXXX
 * - +974 (Qatar): XXXX XXXX
 * - +57 (Colombia): XXX XXX XXXX
 * - +90 (Turkey): XXX XXX XXXX
 * - And others...
 * @returns Yup object schema with countryCode and mobileNumber fields
 */
/**
 * NativeWind `md:` breakpoint (768px). The auth content panel fixes its width
 * at md+ and otherwise fills the viewport — a screen-width decision, so it is
 * derived from the measured window width rather than the vendored Gluestack
 * breakpoint hook.
 */
const MD_BREAKPOINT = 768;

/**
 * Inline copy shown when the request failed and nothing usable came back with
 * it (SE-6889).
 *
 * `FormControlError` renders nothing without children, so a rejection with no
 * message would silently be no error at all - the exact "looks like nothing
 * happened" failure this ticket is about. This string exists so the region is
 * never empty, and is only ever reached when the backend and the transport
 * both supplied nothing.
 */
const SIGN_IN_FAILED = "Something went wrong";

/**
 * Inline copy for a resolution that carried no challenge session (SE-6889).
 *
 * Distinct from {@link SIGN_IN_FAILED} because the user situation differs: the
 * request itself succeeded, but no code was issued, so "try again" is
 * genuinely the right next action rather than a guess.
 */
const CHALLENGE_NOT_ISSUED =
  "We could not send your code. Please try again in a moment.";

/**
 * Treats a blank backend message as no message at all (SE-6889).
 * @remarks
 * `??` falls back only on null and undefined, so `message: ""` passed straight
 * through and rendered an empty inline region and an empty toast — this
 * ticket's own symptom, reintroduced through the fix for it. `formatError`
 * screens blanks on every arm, but `signIn`'s own `message` field never goes
 * through `formatError`, so it needs the same guard here.
 *
 * Trimmed emptiness, not a wider `||`: the thing being detected is "blank",
 * not "falsy".
 * @param message - The backend-supplied message, if any.
 * @returns The message, or undefined when it carries no visible characters.
 */
const presentMessage = (
  message: string | null | undefined
): string | undefined =>
  typeof message === "string" && message.trim().length > 0
    ? message
    : undefined;

const schema = yup.object().shape({
  countryCode: yup.string().required("Can't be blank"),
  mobileNumber: yup
    .string()
    .required("Can't be blank")
    .test("is-valid-phone", "Phone Number must be valid", function (value) {
      const { countryCode } = this.parent; // Get the countryCode from the context

      return validateSignInPhoneNumber(countryCode, value);
    }),
});

/**
 * SignIn Container Component
 *
 * Handles authentication logic for signing in users with phone numbers.
 * Supports multiple country codes with country-specific phone number formatting,
 * validation, and max length calculations.
 *
 * Features:
 * - Country-specific phone number formatting (USA, UK, Mexico, Brazil, Italy, Portugal, Belgium, Greece, Russia, Ukraine, Switzerland, Austria, Czech Republic, Poland, Hungary, Bulgaria, Slovenia, Cyprus, Uruguay, Costa Rica, Qatar, etc.)
 * - Real-time validation with Yup schema
 * - Dynamic max length calculation based on country code
 * - Form state management with React Hook Form
 * - Toast notifications for errors
 * - User goal tracking for sign-in events
 * @returns The SignInView component with all necessary props
 */
const SignInContainer = () => {
  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setError,
    formState: { errors, isValid, isSubmitting },
  } = useForm<IForm>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      countryCode: "+1",
      mobileNumber: "",
    },
    delayError: 1000,
  });
  const toast = useToastNotification();
  const [signIn] = useAuthSignInMutation();
  const recordUserGoal = useRecordUserGoal({
    goalType: GoalType.SignIn,
    value: "Sign In",
  });
  const auth = useAuth();
  const router = useRouter();
  const countryCode = watch("countryCode");
  const inputMobileNumberRef = useRef<ComponentRef<typeof TextInput>>(null);
  const [keyPressMobileNumber, setKeyPressMobileNumber] = useState<string>("");

  const maxLengthInput = useMemo(() => {
    switch (countryCode) {
      case "+34":
      case "+44":
      case "+385": // Croatia
      case "+31": // The Netherlands
        return 11; // 3 digits + space + 3 digits + space + 3 digits
      case "+52": // Mexico
        return 12; // 2 digits + space + 4 digits + space + 4 digits
      case "+54": // Argentina
        return 10; // 10 digits, no separators (no formatting due to variable area codes)
      case "+56": // Chile
        return 11; // 1 digit + space + 4 digits + space + 4 digits
      case "+39": // Italy
        return 12; // 3 digits + space + 3 digits + space + 4 digits
      case "+351": // Portugal
        return 11; // 2 digits + space + 3 digits + space + 4 digits
      case "+32": // Belgium - XXX XXX XXX pattern (9 digits + 2 spaces = 11 chars)
        return 11; // 3 digits + space + 3 digits + space + 3 digits
      // Group F countries: XX XX XX XX pattern (8 digits + 3 spaces = 11 chars)
      case "+352": // Luxembourg
      case "+45": // Denmark
        return 11; // 2 digits + space + 2 digits + space + 2 digits + space + 2 digits
      case "+354": // Iceland
        return 8; // 3 digits + space + 4 digits
      case "+30": // Greece
      // Group E countries: XXX XXX XXXX pattern
      // falls through
      case "+57": // Colombia
      case "+90": // Turkey
        return 12; // 3 digits + space + 3 digits + space + 4 digits
      case "+7": // Russia
        return 15; // ( + 3 digits + ) + space + 3 digits + - + 2 digits + - + 2 digits
      case "+81": // Japan
        return 12; // 2 digits + space + 4 digits + space + 4 digits
      case "+380": // Ukraine
      case "+46": // Sweden - Group A
      case "+40": // Romania - Group A
      case "+358": // Finland - Group A
      case "+593": // Ecuador - Group A
      case "+966": // Saudi Arabia - Group A
      case "+971": // United Arab Emirates - Group A
      case "+64": // New Zealand - Group A
        return 11; // 2 digits + space + 3 digits + space + 4 digits (XX XXX XXXX)
      case "+41": // Switzerland
        return 12; // 2 digits + space + 3 digits + space + 2 digits + space + 2 digits
      case "+43": // Austria
        return 9; // 3 digits + space + 5 digits
      case "+420": // Czech Republic
      case "+48": // Poland
      // Group B countries: XXX XXX XXX pattern (9 digits + 2 spaces = 11 characters)
      // falls through
      case "+421": // Slovakia
      case "+51": // Peru
      case "+595": // Paraguay
      case "+61": // Australia
        return 11; // 3 digits + space + 3 digits + space + 3 digits
      case "+591": // Bolivia
        return 10; // 1 digit + space + 3 digits + space + 4 digits
      // Group C countries: XX XXX XXX pattern (8 digits + 2 spaces = 10 characters)
      case "+36": // Hungary
      case "+359": // Bulgaria
      case "+386": // Slovenia
        return 10; // 2 digits + space + 3 digits + space + 3 digits
      // Group D countries: XXXX XXXX pattern (8 digits + 1 space = 9 characters)
      case "+357": // Cyprus
      case "+598": // Uruguay
      case "+506": // Costa Rica
      case "+974": // Qatar
        return 9; // 4 digits + space + 4 digits
      case "+381": // Serbia
        return 10; // 2 digits + space + 7 digits (XX XXXXXXX)
      case "+47": // Norway
        return 10; // 3 digits + space + 2 digits + space + 3 digits (XXX XX XXX)
      case "+55":
        return 13;
      case "+49": // Germany
        return 14; // Up to 5 digits (area code) + space + up to 8 digits (local number)
      case "+33": // France
        return 13; // 1 digit + space + 2 digits + space + 2 digits + space + 2 digits + space + 2 digits
      case "+353": // Ireland
        return 13; // 3 digits + space + 3 digits + space + 3 digits
      default:
        return 14;
    }
  }, [countryCode]);

  const reportFailure = useCallback(
    (candidate: string | null | undefined, fallback: string) => {
      // SE-6889 (review): the blank check lives HERE, in the one funnel both
      // failure paths go through, rather than at each call site. A call site
      // supplies a CANDIDATE plus the copy to use when there is none; it
      // cannot pass a blank message even by accident, because it never does
      // the falling back itself. That matters because this exact guard has
      // been written correctly in one place and undone in another three times
      // on this ticket - once in formatError, once per call site here.
      const message = presentMessage(candidate) ?? fallback;

      // SE-6889: BOTH halves, always together. The toast is the one that gets
      // noticed; the inline error is the one that is still there afterwards.
      //
      // `action: "danger"` is not cosmetic. Omitting it is the original
      // defect: `useToastNotification` reads `action = "success"`, so a failed
      // sign-in rendered GREEN for 1500ms and then vanished. ConfirmCode has
      // passed `danger` on all three of its equivalent paths since SE-6887 —
      // the two halves of one journey disagreed about what failure looks like.
      toast.show({ description: message, action: "danger" });

      // The durable half. The toast evaporates on a timer, and a user who
      // looked away comes back to a screen that says nothing went wrong and
      // presses the button again — which is the reported retry ladder. The
      // error is attached to `mobileNumber` because that is the field the
      // View's FormControl already renders an error region for, and it is the
      // input the user would have to change to get a different outcome.
      setError("mobileNumber", { type: "server", message });
    },
    [setError, toast]
  );

  const submit = useCallback(
    ({ mobileNumber, countryCode }: IForm) => {
      const completeNumber = `${countryCode}${mobileNumber.replace(/\D/g, "")}`;
      Keyboard.dismiss();
      return signIn({
        onCompleted: () => {
          recordUserGoal();
        },
        variables: { input: { identifier: completeNumber } },
      })
        .then(async result => {
          const signInResult = result.data?.signIn as SignInResult | undefined;

          // SE-6889: `signIn` selects exactly `message` and `data { Session }`,
          // and that Session is the ONLY handle ConfirmCode has — it submits
          // `auth.data.session` and nothing else. `handleCompleteSignIn` only
          // stores a Session it actually received, so navigating without one
          // lands the user on a code screen that submits an empty string, or
          // worse the STALE session left over from a previous attempt. Either
          // way the code they were told to enter cannot work.
          //
          // Today every real failure throws (verified against backend-v2
          // `origin/dev` @ dc02f7d1: every non-success arm of `sendOtpChallenge`
          // raises, and Cognito's CUSTOM_AUTH always returns a Session with a
          // challenge), so this is a guard against a shape the schema permits
          // — `ChallengeResult.Session` is nullable — rather than one seen in
          // production. It is here because the failure it prevents is silent.
          // SE-6889 (review): `useAuth` is `useContext` against a context
          // created with a null default and no throw-if-missing, so its type is
          // `Value | null`. With a null auth, `auth?.onCompleteSignIn(...)`
          // resolves as a no-op and the code screen is reached with nothing
          // stored - the same empty-session dead end this guard exists to
          // prevent, one level up. AuthProvider does wrap the app
          // (src/app/_layout.tsx:213) so it is not reachable today; it is
          // checked for the same reason the Session is, namely that the type
          // permits the shape and the failure it produces is silent. Checking
          // it here is also what lets the call below drop its `?.`.
          if (!auth || !signInResult?.data?.Session) {
            reportFailure(signInResult?.message, CHALLENGE_NOT_ISSUED);
            return;
          }

          // Only reached with a Session in hand, which is also what makes the
          // cast above safe to drop here: the check narrows `signInResult` to
          // present. `handleCompleteSignIn` is what stores the session the
          // confirm-code screen will submit.
          await auth.onCompleteSignIn(signInResult, completeNumber);

          router.push("/confirm-code");
        })
        .catch(e => reportFailure(formatError(e)?.message, SIGN_IN_FAILED));
    },
    [signIn, recordUserGoal, auth, router, reportFailure]
  );

  const doSubmit = handleSubmit(submit);
  const handleDebounceSubmit = useDebouncedCallback(doSubmit, 200);

  const handleSelectionMobileNumber = useCallback(
    (e: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => {
      const { start, end } = e.nativeEvent.selection;
      const currentSymbol = getValues("mobileNumber").slice(start, start + 1);
      if (
        keyPressMobileNumber.toLowerCase() === "backspace" &&
        Platform.OS === "ios" &&
        ["(", ")", " ", ") ", "-"].includes(currentSymbol)
      ) {
        setTimeout(
          () =>
            inputMobileNumberRef.current?.setNativeProps({
              selection: { start, end },
            }),
          100
        );
      }
    },
    [getValues, keyPressMobileNumber]
  );

  const handleKeyPressMobileNumber = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>) =>
      setKeyPressMobileNumber(e.nativeEvent.key),
    []
  );

  const { width } = useWindowDimensions();
  const widthContent = width >= MD_BREAKPOINT ? 750 : width;
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Memoized because `Controller` re-renders the field whenever `render`
  // changes identity — which is what the View's in-JSX `useCallback` was for.
  const renderMobileNumber = useMemo(
    () =>
      makeMobileNumberField({
        getValues,
        errors,
        inputMobileNumberRef: inputMobileNumberRef as RefObject<TextInputProps>,
        isDark,
        loading: isSubmitting,
        maxLengthInput,
        onKeyPressMobileNumber: handleKeyPressMobileNumber,
        onSelectionMobileNumber: handleSelectionMobileNumber,
        onSubmit: handleDebounceSubmit,
      }),
    [
      getValues,
      errors,
      inputMobileNumberRef,
      isDark,
      isSubmitting,
      maxLengthInput,
      handleKeyPressMobileNumber,
      handleSelectionMobileNumber,
      handleDebounceSubmit,
    ]
  );

  useSetPageTitle("Sign In");

  return (
    <SignInView
      valid={isValid}
      widthContent={widthContent}
      loading={isSubmitting}
      control={control}
      onSubmit={handleDebounceSubmit}
      isDark={isDark}
      renderMobileNumber={renderMobileNumber}
    />
  );
};

export default SignInContainer;
